import type { Hlc, Uuid } from './index.js';

/**
 * Hybrid logical clock. See ADR-0008.
 *
 * Never order edits by client wall-clock: one device with a skewed clock would
 * silently win every conflict forever. Never order by server receive time
 * either: that makes whoever reconnects last the winner, so an edit made
 * offline at 09:00 and synced at 17:00 would beat a legitimate 15:00 edit.
 *
 * Lives in `protocol`, not `core-server`: it's pure logic needed identically
 * by the client and both server processes, with no Node- or browser-specific
 * imports of its own.
 */
export class HlcGenerator {
  #last: Hlc;

  constructor(private readonly node: Uuid, private readonly now: () => number = Date.now) {
    this.#last = { wall: 0, counter: 0, node };
  }

  /** Timestamp a locally originated event. */
  send(): Hlc {
    const pt = this.now();
    this.#last = pt > this.#last.wall
      ? { wall: pt, counter: 0, node: this.node }
      : { wall: this.#last.wall, counter: this.#last.counter + 1, node: this.node };
    return this.#last;
  }

  /** Fold a remote timestamp in so causality survives a clock that is behind. */
  receive(remote: Hlc): Hlc {
    const pt = this.now();
    const wall = Math.max(pt, this.#last.wall, remote.wall);
    let counter = 0;
    if (wall === this.#last.wall && wall === remote.wall) {
      counter = Math.max(this.#last.counter, remote.counter) + 1;
    } else if (wall === this.#last.wall) {
      counter = this.#last.counter + 1;
    } else if (wall === remote.wall) {
      counter = remote.counter + 1;
    }
    this.#last = { wall, counter, node: this.node };
    return this.#last;
  }

  get current(): Hlc { return this.#last; }
}

/** Total order. Node id breaks ties so the result is deterministic. */
export function compareHlc(a: Hlc, b: Hlc): number {
  if (a.wall !== b.wall) return a.wall - b.wall;
  if (a.counter !== b.counter) return a.counter - b.counter;
  return a.node < b.node ? -1 : a.node > b.node ? 1 : 0;
}
